import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { LTSSService } from '../../types/ltss';

interface RevenueChartProps {
  services: LTSSService[];
}

export function RevenueChart({ services }: RevenueChartProps) {
  const chartData = useMemo(() => {
    const weeklyData: Record<string, { week: string; revenue: number; units: number }> = {};

    services.forEach(service => {
      const week = service.week || 'Unknown';
      if (!weeklyData[week]) {
        weeklyData[week] = { week, revenue: 0, units: 0 };
      }
      weeklyData[week].revenue += service.amountPaid || 0;
      weeklyData[week].units += service.unitsBilled || 0;
    });

    return Object.values(weeklyData)
      .sort((a, b) => a.week.localeCompare(b.week))
      .slice(-8); // Last 8 weeks
  }, [services]);

  if (chartData.length === 0) {
    return (
      <div className="card p-6 text-center">
        <p className="text-slate-400">No revenue data available</p>
      </div>
    );
  }

  const formatWeek = (week: string) => {
    const match = week.match(/(\d{2})\/(\d{2})/);
    return match ? `${match[1]}/${match[2]}` : week.substring(0, 10);
  };

  return (
    <div className="card p-4">
      <h3 className="text-sm font-medium text-slate-400 mb-4">Weekly Revenue</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              dataKey="week" 
              tickFormatter={formatWeek}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
            />
            <YAxis 
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
              }}
              labelStyle={{ color: '#e2e8f0' }}
              formatter={(value: number, name: string) => [
                name === 'revenue' ? `$${value.toLocaleString()}` : value.toLocaleString(),
                name === 'revenue' ? 'Revenue' : 'Units'
              ]}
              labelFormatter={(label) => `Week: ${label}`}
            />
            <Legend 
              formatter={(value) => <span className="text-slate-300 text-sm">{value === 'revenue' ? 'Revenue' : 'Units'}</span>}
            />
            <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
